import { useMemo, useState } from 'react';
import type { InventoryOverviewItem, LedgerFilters, LedgerReason } from '@/supabase/inventory/types/inventory';
import { EMPTY_LEDGER_FILTERS, hasActiveLedgerFilters } from '@/supabase/inventory/types/inventory';
// InventoryTable과 동일한 non-UI import 허용 — 상품 검색이 norm_name과 동일 정규화 규칙으로
// query를 비교해야 한다(다른 규칙을 쓰면 마스터에 있는 상품이 검색에서 안 잡히는 drift).
import { normalizeItemName } from '@/supabase/inventory/normalizeItemName';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

// 유형 옵션 = LedgerReason DB 계약 전수(임의 축약 금지 — LedgerList의 REASON_TONE Record가
// 전수 커버를 강제하는 것과 동일한 이유. 유니온에 값이 추가되면 이 배열도 함께 갱신할 것).
const REASON_OPTIONS: LedgerReason[] = ['입고', '출고', '조정', '신규등록', '엑셀등록', '주문차감', '주문복원', '주문변경'];

interface LedgerFilterBarProps {
  // 활성 마스터만(useInventory items 재사용) — 소프트 삭제된 상품의 원장 행은 필터 대상으로
  // 선택할 수 없다(알려진 한계, 브리프 승인 사항: 삭제 상품용 별도 조회를 두지 않는다).
  items: InventoryOverviewItem[];
  // 상품 목록의 로딩/오류 상태 — 없으면 콤보박스가 "검색 결과가 없어요"로 오표시한다
  // (codexloop PR7 R1 P2 부분수용: 카피 분기만. 재시도 버튼은 목록 탭 InventoryTable 소관).
  isItemsLoading?: boolean;
  hasItemsError?: boolean;
  // 선택된 itemId가 활성 목록(items)에 없을 때(필터 적용 후 소프트 삭제) 쓸 이름 폴백 —
  // 현재 로드된 원장 행의 item_name 스냅샷에서 유도(탭 전환 시 이 컴포넌트가 unmount되므로
  // 로컬 스냅샷은 정확히 그 시나리오에서 살아남지 못한다. codexloop PR7 R1 P2 부분수용).
  selectedItemFallbackName?: string | null;
  filters: LedgerFilters;
  onChange: (next: LedgerFilters) => void;
  disabled?: boolean;
}

/**
 * 입출고 내역 필터 바(PR7, dumb 컴포넌트). 필터 값은 전부 부모(useInventoryLedger 경유)가
 * 소유하고, 여기는 표시 + onChange 통지만 한다 — 콤보박스 열림/검색어처럼 이 바에서만 읽는
 * UI state만 콜로케이션(CLAUDE.md 규율 6).
 */
export function LedgerFilterBar({
  items,
  isItemsLoading,
  hasItemsError,
  selectedItemFallbackName,
  filters,
  onChange,
  disabled,
}: LedgerFilterBarProps) {
  const [isProductOpen, setIsProductOpen] = useState(false);
  const [productQuery, setProductQuery] = useState('');

  const filteredItems = useMemo(() => {
    const normQuery = normalizeItemName(productQuery);
    if (!normQuery) return items;
    return items.filter((item) => item.norm_name.includes(normQuery));
  }, [items, productQuery]);

  const selectedItem = filters.itemId === null ? null : items.find((item) => item.id === filters.itemId) ?? null;
  // itemId가 있는데 items에 없는 경우 = 필터 적용 후 그 상품이 소프트 삭제돼 활성 목록에서
  // 빠진 상태. 필터 자체는 여전히 유효(원장 쿼리는 id 기준)하므로 원장 행 스냅샷 이름 폴백 →
  // 그것도 없으면(필터 결과 0건 등) 대체 문구로 남겨 사용자가 해제할 수 있게 한다.
  // '상품:' 접두는 유형 필터('유형: 전체/입고')와 라벨 문법 통일(UI 폴리시 — 선택 전엔
  // '상품: 전체'인데 선택 후 접두가 사라지면 어느 필터의 값인지 한 박자 늦게 읽힌다).
  const productLabel = selectedItem
    ? `상품: ${selectedItem.name}`
    : filters.itemId !== null
      ? `상품: ${selectedItemFallbackName ?? '선택한 상품'}`
      : '상품: 전체';

  const isFiltered = hasActiveLedgerFilters(filters);

  // 'YYYY-MM-DD'는 사전순 비교가 곧 날짜 비교라 문자열 부등호로 충분. from > to로 어긋나면
  // 변경한 쪽 기준으로 반대쪽을 같은 값으로 보정한다(어긋난 범위를 그대로 두면 항상 무결과라
  // 사용자가 "왜 안 나오지" 혼란 — 브리프 명시).
  const handleDateFromChange = (value: string) => {
    const dateFrom = value === '' ? null : value;
    const next: LedgerFilters = { ...filters, dateFrom };
    if (dateFrom !== null && next.dateTo !== null && dateFrom > next.dateTo) next.dateTo = dateFrom;
    onChange(next);
  };
  const handleDateToChange = (value: string) => {
    const dateTo = value === '' ? null : value;
    const next: LedgerFilters = { ...filters, dateTo };
    if (dateTo !== null && next.dateFrom !== null && dateTo < next.dateFrom) next.dateFrom = dateTo;
    onChange(next);
  };

  const handleProductOpenChange = (open: boolean) => {
    setIsProductOpen(open);
    // 열 때마다 검색어 초기화 — 이전 검색어 잔상 탓에 목록이 비어 보이는 혼란 방지.
    if (open) setProductQuery('');
  };

  // 동일 값 재선택은 no-op — 같은 필터로 불필요한 전체 reload(원장 + 이름 2차 쿼리)가 나가는
  // 것을 막는다(codexloop PR7 R1 P2 부분수용: 값-동등 가드만. 날짜 input은 native onChange가
  // 동일 값에 발화하지 않아 가드 불필요).
  const handleSelectProduct = (itemId: number | null) => {
    setIsProductOpen(false);
    if (itemId === filters.itemId) return;
    onChange({ ...filters, itemId });
  };

  const handleSelectReason = (reason: LedgerReason | null) => {
    if (reason === filters.reason) return;
    onChange({ ...filters, reason });
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input
        type="date"
        value={filters.dateFrom ?? ''}
        onChange={(e) => handleDateFromChange(e.target.value)}
        aria-label="시작일"
        disabled={disabled}
        className="h-8 w-36 sm:w-40"
      />
      <span className="text-sm text-slate-400">~</span>
      <Input
        type="date"
        value={filters.dateTo ?? ''}
        onChange={(e) => handleDateToChange(e.target.value)}
        aria-label="종료일"
        disabled={disabled}
        className="h-8 w-36 sm:w-40"
      />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={disabled}>
            유형: {filters.reason ?? '전체'}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onSelect={() => handleSelectReason(null)}>전체</DropdownMenuItem>
          {REASON_OPTIONS.map((reason) => (
            <DropdownMenuItem key={reason} onSelect={() => handleSelectReason(reason)}>
              {reason}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Popover open={isProductOpen} onOpenChange={handleProductOpenChange}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" disabled={disabled}>
            {/* Button 베이스가 whitespace-nowrap이라 긴 상품명은 자식 truncate로만 자른다
                (break-all은 nowrap과 충돌해 죽는다 — 브리프 함정 명시). */}
            <span className="max-w-[160px] truncate">{productLabel}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-2">
          <Input
            value={productQuery}
            onChange={(e) => setProductQuery(e.target.value)}
            placeholder="상품명 검색"
            aria-label="필터할 상품명 검색"
            className="h-8"
          />
          <div className="mt-2 max-h-64 space-y-0.5 overflow-y-auto">
            <button
              type="button"
              onClick={() => handleSelectProduct(null)}
              className={`block w-full truncate rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-slate-100 ${
                filters.itemId === null ? 'bg-slate-100 font-medium text-slate-900' : 'text-slate-700'
              }`}
            >
              전체 상품
            </button>
            {filteredItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleSelectProduct(item.id)}
                title={item.name}
                className={`block w-full truncate rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-slate-100 ${
                  filters.itemId === item.id ? 'bg-slate-100 font-medium text-slate-900' : 'text-slate-700'
                }`}
              >
                {item.name}
              </button>
            ))}
            {filteredItems.length === 0 && (
              // items 자체가 빈 건 "검색 무결과"가 아니라 로딩 중/로드 실패/미등록일 수 있다 —
              // 상태별 정직한 카피로 분기(codexloop PR7 R1 P2 부분수용).
              <p className="break-keep px-2 py-1.5 text-sm text-slate-400">
                {items.length > 0
                  ? '검색 결과가 없습니다'
                  : isItemsLoading
                    ? '상품 목록을 불러오는 중...'
                    : hasItemsError
                      ? '상품 목록을 불러오지 못했습니다 — 재고 목록 탭에서 다시 시도해 주세요'
                      : '등록된 상품이 없습니다'}
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {isFiltered && (
        <Button variant="outline" size="sm" onClick={() => onChange(EMPTY_LEDGER_FILTERS)} disabled={disabled}>
          필터 초기화
        </Button>
      )}
    </div>
  );
}
